import { Box, Button, Divider, Tooltip } from '@mui/material';
import { useLocation, useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import { EditableName } from 'src/components/molecules/editable-name';
import { PageHeader } from 'src/components/molecules/page-header';
import Page from 'src/components/organisms/page/page';
import { Pagination } from 'src/components/organisms/pagination';
import { fetchSpaces } from 'src/features/spaces/api/lib/spaces';
import { spacesKeys } from 'src/features/spaces/api/query-keys/spaces';
import { useMeilisearch } from 'src/hooks/meilisearch/use-meilisearch';
import { useMeilisearchWithUrlParams } from 'src/hooks/meilisearch/use-meilisearch-with-url-params';
import { useDialog } from 'src/hooks/use-dialog';
import { useSortController } from 'src/hooks/use-sort-controller';
import { Plus } from 'src/icons/plus';
import { Can, withPermissions } from 'src/permissions/can';
import { Route } from 'src/routes/_layout/tenant/spaces/groups/$spaceGroupToken';
import { useUserStore } from 'src/store/user-store';
import { SortOrder } from 'src/types/enum/sort-order';
import { useUpdateSpaceGroupName } from '../../api/hooks/use-update-space-group-name';
import { fetchSpaceGroups } from '../../api/lib/space-groups';
import { spaceGroupsKeys } from '../../api/query-keys/space-groups';
import { SpaceStateFilterPopover } from '../molecules/filters/space-state-filter-popover';
import { SpacesFilterChips } from '../molecules/filters/spaces-filter-chips';
import { AssignSpaceToSpaceGroupDialog } from '../organisms/dialogs/assign-space-to-space-group-dialog';
import { DeleteSpaceFromSpaceGroupDialog } from '../organisms/dialogs/delete-space-from-space-group-dialog/delete-space-from-space-group-dialog';
import { SpacesMoreFilters } from '../organisms/spaces-more-filters';
import { SpacesTable } from '../organisms/spaces-table';

const SpaceGroupDetails = () => {
  const { spaceGroupToken } = Route.useParams();

  const { query, perPage, page, filter } = useSearch({
    from: '/_layout/tenant/spaces/groups/$spaceGroupToken',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  const { results: spaceGroupResults } = useMeilisearch({
    fetch: fetchSpaceGroups,
    queryKey: spaceGroupsKeys.detail(spaceGroupToken),
    page: 1,
    perPage: 1,
    searchState: {
      filter: [[`token = ${spaceGroupToken}`]],
      q: '',
      sort: [],
    },
  });

  const spaceGroup = spaceGroupResults?.hits?.[0];

  const { directoryToken } = useUserStore();

  const [sortController, setSortController] = useSortController({
    sortBy: 'name',
    sortOrder: SortOrder.DESC,
  });

  const defaultSearchStateConfig = useMemo(() => {
    return {
      filter: {
        spaceGroupToken: {
          value: spaceGroupToken,
        },
      },
    };
  }, [spaceGroupToken]);

  const {
    results: spacesResults,
    handleChangeQuery,
    handleChangePage,
    isLoading,
    isFetching,
    handleChangeLimit,
    error,
    handleChangeFilterValue,
    handleChangeSort,
    resetFilters,
  } = useMeilisearchWithUrlParams(
    fetchSpaces,
    spacesKeys.list(directoryToken ?? '', page, perPage),
    defaultSearchStateConfig,
  );

  const location = useLocation();

  const { t } = useTranslation(['spaceGroups', 'common']);

  //Single space action
  const [selectedSpaceToken, setSelectedSpaceToken] = useState<string | null>(
    null,
  );

  const [
    isOpenDeleteSpaceFromGroupDialog,
    handleOpenDeleteSpaceFromGroupDialog,
    handleCloseDeleteSpaceFromGroupDialog,
  ] = useDialog();

  const [
    isOpenAssignSpaceToSpaceGroupDialog,
    handleOpenAssignSpaceToSpaceGroupDialog,
    handleCloseAssignSpaceToSpaceGroupDialog,
  ] = useDialog();

  const {
    mutateAsync: updateSpaceGroupName,
    isPending: isUpdatingSpaceGroupName,
  } = useUpdateSpaceGroupName();

  const totalPages = Math.ceil(
    (spacesResults?.estimatedTotalHits ?? 0) / perPage,
  );

  return (
    <>
      <Page>
        <Page.BackButton
          href={location.state.backUrl ?? '/tenant/spaces/groups'}
        />

        <PageHeader
          title=""
          extras={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flex: 1,
              }}>
              <EditableName
                name={spaceGroup?.name ?? ''}
                onUpdateName={name =>
                  updateSpaceGroupName({ token: spaceGroupToken, name })
                }
                isLoadingName={isUpdatingSpaceGroupName}
              />

              <Can I="assignSpaces" a="SpaceGroups" passThrough>
                {allowed => (
                  <Tooltip
                    title={t('common:notSufficientPermissions')}
                    disableHoverListener={allowed}
                    disableFocusListener={allowed}>
                    <span>
                      <Button
                        sx={{ ml: 2 }}
                        onClick={handleOpenAssignSpaceToSpaceGroupDialog}
                        color="primary"
                        disabled={!allowed}
                        startIcon={<Plus fontSize="small" />}
                        variant="contained"
                        id="assign_space_button"
                        size="large">
                        {t('spaceGroups:details.assignSpaces')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Can>
            </Box>
          }
        />

        <Page.ContentContainer>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              px: 3,
              py: 2.5,
            }}>
            <Query
              searchAsType
              disabled={false}
              onChange={handleChangeQuery}
              sx={{
                minWidth: 200,
              }}
              value={query}
            />

            <SpaceStateFilterPopover
              filters={filter}
              handleChangeFilterValue={handleChangeFilterValue}
            />

            <SpacesMoreFilters
              anchor={'right'}
              filters={filter}
              handleChangeFilterValue={handleChangeFilterValue}
              resetFilters={resetFilters}
            />
          </Box>

          <SpacesFilterChips
            filters={filter}
            handleChangeFilterValue={handleChangeFilterValue}
          />

          <SpacesTable
            data={spacesResults?.hits ?? []}
            isLoading={isLoading}
            error={error?.response?.data?.message}
            sortController={sortController}
            setSortController={setSortController}
            handleChangeSort={handleChangeSort}
            onRemoveFromGroup={(spaceToken: string) => {
              setSelectedSpaceToken(spaceToken);
              handleOpenDeleteSpaceFromGroupDialog();
            }}
          />

          <Divider sx={{ mt: 'auto' }} />

          <Pagination
            disabled={isLoading}
            isFetching={isFetching}
            changePage={handleChangePage}
            rowsCount={spacesResults?.estimatedTotalHits || 0}
            page={page}
            perPage={perPage}
            totalPages={totalPages}
            handlePerPageChange={handleChangeLimit}
          />
        </Page.ContentContainer>
      </Page>

      <DeleteSpaceFromSpaceGroupDialog
        open={isOpenDeleteSpaceFromGroupDialog}
        onCancel={() => {
          handleCloseDeleteSpaceFromGroupDialog();
        }}
        onConfirm={() => {
          handleCloseDeleteSpaceFromGroupDialog();
          setSelectedSpaceToken(null);
        }}
        spaceGroupToken={spaceGroupToken}
        spaceGroupName={spaceGroup?.name ?? ''}
        spaceToken={selectedSpaceToken ?? ''}
      />

      <AssignSpaceToSpaceGroupDialog
        open={isOpenAssignSpaceToSpaceGroupDialog}
        onCancel={handleCloseAssignSpaceToSpaceGroupDialog}
        spaceGroupToken={spaceGroupToken}
      />
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'SpaceGroups'),
  SpaceGroupDetails,
);
